import { IStorageAdapter } from './types';
import { TrimbleIdentityService } from './trimble-identity-service';
import { createPKCEManager } from './pkce';

/**
 * Provides a global TrimbleIdentityService instance that can be shared across the application.
 *
 * In addition to the default singleton, the provider can host any number of
 * independent "named" instances (for example, one per extension `appId`).
 * Named instances have their own storage session key and an isolated PKCE
 * storage namespace so that they do not interfere with the default instance
 * or with each other.
 */
class TrimbleIdentityServiceProvider {
  private static instance: TrimbleIdentityService | null = null;
  private static _storageAdapter: IStorageAdapter | null = null;
  private static readonly namedInstances: Map<string, TrimbleIdentityService> =
    new Map();

  /**
   * Initialize the singleton instance
   * @param storageAdapter Storage adapter implementation
   * @param sessionKey Optional session key array (defaults to standard key based on environment)
   */
  public static initialize(
    storageAdapter: IStorageAdapter,
    sessionKey?: string[]
  ): TrimbleIdentityService {
    if (!this.instance) {
      this.instance = new TrimbleIdentityService(storageAdapter, sessionKey);
      this._storageAdapter = storageAdapter;
    }
    return this.instance;
  }

  /**
   * Get the singleton instance
   * @throws Error if instance is not initialized
   */
  public static getInstance(): TrimbleIdentityService {
    if (!this.instance) {
      throw new Error(
        'TrimbleIdentityService not initialized. Call initialize() first.'
      );
    }
    return this.instance;
  }

  /**
   * Get the storage adapter
   */
  public static getStorageAdapter(): IStorageAdapter | null {
    return this._storageAdapter;
  }

  /**
   * Check if the singleton is initialized
   */
  public static isInitialized(): boolean {
    return this.instance !== null;
  }

  /**
   * Initialize (or return the already-initialized) named instance.
   *
   * Named instances are intended for scenarios where the host app needs to
   * maintain multiple independent identity sessions (for example, one per
   * extension `appId`). The PKCE storage for a named instance is namespaced
   * by `name` so it does not collide with the default instance.
   *
   * @param name Unique identifier for the instance (e.g. extension appId)
   * @param storageAdapter Storage adapter implementation
   * @param sessionKey Session key array used to persist the instance's session
   * @returns The named TrimbleIdentityService instance
   */
  public static initializeNamed(
    name: string,
    storageAdapter: IStorageAdapter,
    sessionKey: string[]
  ): TrimbleIdentityService {
    const existing = this.namedInstances.get(name);
    if (existing) {
      return existing;
    }
    const pkceManager = createPKCEManager(storageAdapter, false, name);
    const instance = new TrimbleIdentityService(
      storageAdapter,
      sessionKey,
      pkceManager
    );
    this.namedInstances.set(name, instance);
    return instance;
  }

  /**
   * Get a named instance, if initialized.
   * @param name Unique identifier for the instance
   */
  public static getNamedInstance(
    name: string
  ): TrimbleIdentityService | undefined {
    return this.namedInstances.get(name);
  }

  /**
   * Whether a named instance exists.
   */
  public static hasNamedInstance(name: string): boolean {
    return this.namedInstances.has(name);
  }

  /**
   * Remove a named instance from the provider.
   * Callers are responsible for any cleanup on the instance (e.g. signing out
   * or clearing the session) before calling this method.
   *
   * @param name Unique identifier for the instance
   */
  public static disposeNamed(name: string): void {
    this.namedInstances.delete(name);
  }

  /**
   * Reset the singleton (useful for testing)
   */
  public static reset(): void {
    this.instance = null;
    this._storageAdapter = null;
    this.namedInstances.clear();
  }
}

export default TrimbleIdentityServiceProvider;
